import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../../services/clinical-onboarding.service';
import { ToastService } from '../../../services/general/toast.service';
import {
  ClinicianActivityEvent,
  ClinicianCandidateDocument,
  ClinicianCandidateDocumentStatus,
  ClinicianDetailResponse,
  ClinicianDocumentTemplateScope,
  clinicalOnboardingI18nKeySuffix,
} from '../../../models/clinical-onboarding.model';
import { ModalEditClinicianDataComponent } from '../../../modals/modal-edit-clinician-data/modal-edit-clinician-data.component';
import { ModalAddCandidateDocumentComponent } from '../../../modals/modal-add-candidate-document/modal-add-candidate-document.component';

/** Categorie dello storico attivita' (feedback utente: "c'e' bisogno di filtri e ricerca"). */
const ACTIVITY_CATEGORIES = {
  ALL: 'ALL',
  REGISTRATION: 'REGISTRATION',
  DOCUMENTS: 'DOCUMENTS',
  PAYMENT: 'PAYMENT',
  REMINDERS: 'REMINDERS',
  OTHER: 'OTHER',
} as const;

interface ActivityRow {
  icon: string;
  i18nKey: string;
  params: Record<string, any>;
  created_at: string;
  actor_email: string | null;
  category: string;
}

/**
 * DBEK-392: pannello di dettaglio di un clinico, aperto dalla Tabella-1 della
 * dashboard (DBEK-407). Punto di integrazione reale per l'azione di
 * verifica-documento (DBEK-402) e - feedback utente ("tracking professionale e
 * completo... raggiungibile dalla tabella di onboarding") - dello storico attivita'
 * completo (tab dedicata, caricata solo alla prima apertura per non appesantire
 * l'apertura del pannello quando il gestore guarda solo lo stato corrente).
 * Conferma pagamento/override/esclusione restano fuori da questo ticket - nessuno
 * dei ticket originali della epic li copriva esplicitamente.
 */
@Component({
  selector: 'app-clinical-onboarding-clinician-detail-dialog',
  templateUrl: './clinical-onboarding-clinician-detail-dialog.component.html',
  styleUrls: ['./clinical-onboarding-clinician-detail-dialog.component.scss'],
  standalone: false,
})
export class ClinicalOnboardingClinicianDetailDialogComponent implements OnInit {
  loading = true;
  detail: ClinicianDetailResponse | null = null;
  readonly ClinicianDocumentTemplateScope = ClinicianDocumentTemplateScope;
  readonly ClinicianCandidateDocumentStatus = ClinicianCandidateDocumentStatus;

  activityRows: ActivityRow[] = [];
  activityLoading = false;
  private activityLoaded = false;
  readonly ActivityCategories = ACTIVITY_CATEGORIES;
  activitySearchTerm = '';
  activityCategoryFilter: string = ACTIVITY_CATEGORIES.ALL;

  uploadingDocumentId: number = null;

  // Feedback utente: il dialog ha ora 4 tab distinte (Dettagli/Dati contrattuali/
  // Documenti/Storico) - "Dettagli" modifica nome/cognome/email direttamente in
  // pagina (niente piu' modale per questi 3 campi), "Dati contrattuali" resta
  // sola-lettura + modale per il resto, come gia' prima.
  identityForm: FormGroup;
  savingIdentity = false;
  savingVerificationOverride = false;

  constructor(
    public dialogRef: MatDialogRef<ClinicalOnboardingClinicianDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: string | number; candidateId: number },
    private clinicalOnboardingService: ClinicalOnboardingService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.clinicalOnboardingService.getClinicianDetail(
      this.data.surveyId,
      this.data.candidateId,
      (result) => {
        this.loading = false;
        this.detail = result;
        // Feedback bug: un'azione su un'altra tab (upload documento, verifica,
        // override IBAN) richiama load() e ricostruiva sempre identityForm da
        // zero, cancellando in silenzio modifiche non salvate nel tab "Dettagli".
        // Non tocchiamo il form se l'utente ci ha gia' digitato qualcosa.
        if (!this.identityForm || !this.identityForm.dirty) {
          this.rebuildIdentityForm();
        }
      },
      () => {
        this.loading = false;
      },
    );
  }

  /**
   * Ricostruito ad ogni load() (non solo alla prima apertura) cosi' riflette
   * sempre l'ultimo dato salvato - anche dopo un salvataggio riuscito da questo
   * stesso form, o da un'azione su un'altra tab che ricarica il dettaglio.
   */
  private rebuildIdentityForm(): void {
    this.identityForm = this.fb.group({
      name: [this.detail.candidate.first_name, Validators.required],
      last_name: [this.detail.candidate.last_name || ''],
      email: [this.detail.candidate.email, [Validators.required, Validators.email]],
    });
  }

  /** Tab "Dettagli": nome/cognome/email, modificabili direttamente qui - stesso gate di "Dati contrattuali" (bloccato dopo il pagamento, l'intero endpoint lo rifiuterebbe comunque). */
  saveIdentity(): void {
    if (this.identityForm.invalid) {
      this.identityForm.markAllAsTouched();
      return;
    }

    this.savingIdentity = true;
    const value = this.identityForm.value;

    this.clinicalOnboardingService.updateClinicianData(
      this.data.surveyId,
      this.data.candidateId,
      {
        name: value.name,
        last_name: value.last_name || null,
        email: value.email,
      },
      () => {
        this.savingIdentity = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.clinician-detail.data-saved'),
        );
        // Un cambio email rinnova l'invito - invalida lo storico gia' caricato
        // cosi' la prossima apertura di quella tab mostra il nuovo evento.
        this.activityLoaded = false;
        this.load();
      },
      () => {
        this.savingIdentity = false;
      },
    );
  }

  /**
   * Feedback utente: "quando aggiungo il clinico [...] mi dice nessuna anomalia
   * iban, ma l'iban non c'è proprio salvato" - la derivazione automatica (vedi
   * ClinicianEngagement::getVerificationOkAttribute) considera "nessun IBAN"
   * come "nessun problema" (verification_ok=true), ma etichettarlo "IBAN valido"
   * e' fuorviante: nessun IBAN e' stato davvero controllato. Un terzo stato
   * "neutro" (nessun IBAN ancora inserito) distingue questo caso da una verifica
   * automatica REALMENTE superata, senza toccare verification_ok stesso (resta
   * "non bloccante" ai fini del conteggio KPI "Verificati OK" - qui cambia solo
   * l'etichetta/lo stile mostrati).
   */
  get verificationDisplay(): { icon: string; labelKey: string; statusClass: 'ok' | 'ko' | 'neutral' } {
    const engagement = this.detail?.engagement;
    if (!engagement) {
      return { icon: 'help_outline', labelKey: '', statusClass: 'neutral' };
    }

    if (engagement.verification_override !== null) {
      return engagement.verification_ok
        ? { icon: 'check_circle', labelKey: 'components.clinical-onboarding.clinician-detail.verification-override-valid', statusClass: 'ok' }
        : { icon: 'cancel', labelKey: 'components.clinical-onboarding.clinician-detail.verification-override-invalid', statusClass: 'ko' };
    }

    if (!engagement.verification_ok) {
      return { icon: 'cancel', labelKey: 'components.clinical-onboarding.dashboard.verification-ko', statusClass: 'ko' };
    }

    if (!engagement.has_iban) {
      return { icon: 'info', labelKey: 'components.clinical-onboarding.clinician-detail.verification-no-iban-yet', statusClass: 'neutral' };
    }

    return { icon: 'check_circle', labelKey: 'components.clinical-onboarding.dashboard.verification-ok', statusClass: 'ok' };
  }

  /**
   * Feedback utente: "permetti all'amministratore di metterlo come valido o non
   * valido a prescindere [...] gerarchicamente più importante della verifica
   * iban fatta dal sistema [...] override permanente" - value=true/false forza
   * il semaforo "Verifica", null lo rimuove (torna alla verifica automatica).
   * name/last_name/email sono comunque richiesti dal server: passo quelli gia'
   * presenti, invariati (nessun campo visibile per loro qui).
   */
  setVerificationOverride(value: boolean | null): void {
    this.savingVerificationOverride = true;

    this.clinicalOnboardingService.updateClinicianData(
      this.data.surveyId,
      this.data.candidateId,
      {
        name: this.detail.candidate.first_name,
        last_name: this.detail.candidate.last_name || null,
        email: this.detail.candidate.email,
        verification_override: value,
      },
      () => {
        this.savingVerificationOverride = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.clinician-detail.data-saved'),
        );
        this.activityLoaded = false;
        this.load();
      },
      () => {
        this.savingVerificationOverride = false;
      },
    );
  }

  /** DBEK-413: il gestore modifica i dati contrattuali del clinico. */
  openEditData(): void {
    const dialogRef = this.dialog.open(ModalEditClinicianDataComponent, {
      data: { surveyId: this.data.surveyId, detail: this.detail },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Un cambio di email rinnova l'invito, un cambio di datore di lavoro puo'
        // cambiare pa_authorization_status - invalida lo storico gia' caricato
        // cosi' la prossima apertura di quella tab mostra i nuovi eventi.
        this.activityLoaded = false;
        this.load();
      }
    });
  }

  /**
   * DBEK-412: il gestore allega un documento riservato a QUESTO candidato,
   * direttamente dalla sua scheda - non comparirà mai tra i "Documenti condivisi".
   */
  openAddDocument(): void {
    const dialogRef = this.dialog.open(ModalAddCandidateDocumentComponent, {
      data: { surveyId: this.data.surveyId, candidateId: this.data.candidateId },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.activityLoaded = false;
        this.load();
      }
    });
  }

  /**
   * Caricato solo alla prima apertura della tab "Storico" - indice 3 con le 4 tab
   * (Dettagli/Dati contrattuali/Documenti/Storico), non piu' 1 come quando era la
   * seconda di sole due tab.
   */
  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 3 && !this.activityLoaded) {
      this.loadActivity();
    }
  }

  loadActivity(): void {
    this.activityLoading = true;
    this.activityLoaded = true;
    this.clinicalOnboardingService.getClinicianActivity(
      this.data.surveyId,
      this.data.candidateId,
      (result) => {
        this.activityLoading = false;
        this.activityRows = (result.events || []).map((event) => this.toActivityRow(event));
      },
      () => {
        this.activityLoading = false;
      },
    );
  }

  /**
   * Un evento grezzo (event + payload) in una riga pronta per il template - un
   * unico punto dove tradurre il vocabolario tecnico di ClinicianAuditTrail in un
   * messaggio leggibile, invece di uno @switch enorme nell'HTML. Evento non
   * riconosciuto (nuovo tipo aggiunto in futuro lato backend senza aggiornare qui):
   * fallback sul nome tecnico grezzo, mai una riga vuota o un crash.
   */
  private toActivityRow(event: ClinicianActivityEvent) {
    const p = event.payload || {};
    const base = { created_at: event.created_at, actor_email: event.actor_email };
    const prefix = 'components.clinical-onboarding.activity.';

    const map: Record<string, { icon: string; i18nKey: string; params?: Record<string, any> }> = {
      'candidate.registered': { icon: 'person_add', i18nKey: prefix + 'registered' },
      'candidate.invitation_sent': { icon: 'mail', i18nKey: prefix + 'invitation-sent' },
      'candidate.invitation_renewed': { icon: 'mail', i18nKey: prefix + 'invitation-renewed' },
      'candidate.invitation_accepted': { icon: 'login', i18nKey: prefix + 'invitation-accepted' },
      'candidate.imported': { icon: 'group_add', i18nKey: prefix + 'imported' },
      'candidate.reminder.manual': { icon: 'notifications_active', i18nKey: prefix + 'reminder-manual' },
      'candidate.reminder.automatic': { icon: 'notifications', i18nKey: prefix + 'reminder-automatic', params: { stage: p.stage } },
      'candidate.deadline_expired': { icon: 'event_busy', i18nKey: prefix + 'deadline-expired' },
      'clinician_engagement.updated_by_clinician': { icon: 'edit', i18nKey: prefix + 'engagement-updated' },
      'clinician_engagement.updated_by_manager': { icon: 'edit', i18nKey: prefix + 'engagement-updated-by-manager' },
      // Feedback utente: override permanente sul semaforo "Verifica" - tre esiti
      // distinti (impostato valido / impostato non valido / rimosso), non un
      // singolo evento generico, cosi' lo storico dice esattamente cosa e'
      // successo invece di un anonimo "dati aggiornati".
      'clinician_engagement.verification_overridden': {
        icon: p.override === null ? 'restart_alt' : (p.override ? 'check_circle' : 'cancel'),
        i18nKey: prefix + (p.override === null
          ? 'verification-overridden-cleared'
          : p.override
            ? 'verification-overridden-valid'
            : 'verification-overridden-invalid'),
      },
      'clinician_candidate_document.added_by_manager': { icon: 'note_add', i18nKey: prefix + 'document-added-by-manager', params: { title: p.title } },
      'clinician_candidate_document.viewed': { icon: 'visibility', i18nKey: prefix + 'document-viewed', params: { title: p.title } },
      'clinician_candidate_document.returned': { icon: 'upload_file', i18nKey: prefix + 'document-returned', params: { title: p.title } },
      'clinician_candidate_document.return_viewed': { icon: 'visibility', i18nKey: prefix + 'document-return-viewed', params: { title: p.title } },
      'clinician_candidate_document.own_return_viewed': { icon: 'visibility', i18nKey: prefix + 'document-own-return-viewed', params: { title: p.title } },
      'clinician_candidate_document.file_uploaded': { icon: 'upload_file', i18nKey: prefix + 'document-file-uploaded', params: { title: p.title } },
      'clinician_candidate_document.status_changed': {
        icon: p.to_status === 'ANOMALY' ? 'error' : 'verified',
        i18nKey: prefix + 'document-status-changed',
        params: { title: p.title, from: p.from_status, to: p.to_status },
      },
      'clinician_payment.status_changed': { icon: 'payments', i18nKey: prefix + 'payment-status-changed', params: { from: p.from_status, to: p.to_status } },
    };

    const entry = map[event.event] || { icon: 'info', i18nKey: null, params: { event: event.event } };
    const i18nKey = entry.i18nKey || (prefix + 'unknown');
    const params = entry.params || {};

    return {
      ...base,
      icon: entry.icon,
      i18nKey,
      params,
      category: this.categoryFor(event.event),
    };
  }

  /** Categorie dello storico (feedback utente: "c'e' bisogno di filtri e ricerca"). */
  private categoryFor(event: string): string {
    if (event.startsWith('clinician_candidate_document.')) {
      return ACTIVITY_CATEGORIES.DOCUMENTS;
    }
    if (event.startsWith('clinician_payment.')) {
      return ACTIVITY_CATEGORIES.PAYMENT;
    }
    if (event.startsWith('candidate.reminder.') || event === 'candidate.deadline_expired') {
      return ACTIVITY_CATEGORIES.REMINDERS;
    }
    if (event.startsWith('candidate.')) {
      return ACTIVITY_CATEGORIES.REGISTRATION;
    }
    return ACTIVITY_CATEGORIES.OTHER;
  }

  /**
   * Filtro combinato categoria + ricerca testuale (sul messaggio gia' tradotto e
   * sull'email dell'attore) - tutto lato client: lo storico di un candidato e' gia'
   * interamente caricato in una sola chiamata (getClinicianActivity), non serve
   * un endpoint paginato/filtrato per liste di questa taglia.
   */
  get filteredActivityRows(): ActivityRow[] {
    const term = this.activitySearchTerm.trim().toLowerCase();

    return this.activityRows.filter((row) => {
      if (this.activityCategoryFilter !== ACTIVITY_CATEGORIES.ALL && row.category !== this.activityCategoryFilter) {
        return false;
      }
      if (!term) {
        return true;
      }
      // Bug cambio lingua: la label era tradotta una volta sola al caricamento e
      // restava congelata nella lingua di allora. Ritraducendo qui ad ogni
      // ricerca, sia il filtro sia la label mostrata nel template (via pipe
      // "translate") seguono sempre la lingua corrente.
      const label = this.translateService.instant(row.i18nKey, row.params).toLowerCase();
      return label.includes(term) || (row.actor_email || '').toLowerCase().includes(term);
    });
  }

  onDocumentStatusChanged(document: ClinicianCandidateDocument, updated: ClinicianCandidateDocument): void {
    const index = this.detail.documents.findIndex((d) => d.id === document.id);
    if (index >= 0) {
      this.detail.documents[index] = updated;
    }
    // Il macro_status dell'engagement potrebbe essere cambiato di conseguenza
    // (recompute server-side) - ricarico tutto per riflettere subito lo stato
    // aggiornato invece di provare a ricalcolarlo lato client. Invalida anche lo
    // storico gia' caricato, cosi' la prossima apertura di quella tab mostra il
    // nuovo evento invece di una copia stantia.
    this.activityLoaded = false;
    this.load();
  }

  viewDocument(document: ClinicianCandidateDocument, which: 'source' | 'return'): void {
    this.clinicalOnboardingService.openCandidateDocument(this.data.candidateId, document.id, which);
  }

  onSourceFileSelected(document: ClinicianCandidateDocument, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.uploadingDocumentId = document.id;
    this.clinicalOnboardingService.uploadCandidateDocumentFile(
      this.data.surveyId,
      document.id,
      file,
      () => {
        this.uploadingDocumentId = null;
        this.load();
      },
      () => {
        this.uploadingDocumentId = null;
      },
    );

    input.value = '';
  }

  close(): void {
    this.dialogRef.close();
  }

  i18nKeySuffix(value: string): string {
    return clinicalOnboardingI18nKeySuffix(value);
  }
}
